// ./src/components/features/CreateEventWizard/components/EventCreatedScreen.tsx
//
// Terminal screen after the wizard — celebratory success or a recoverable failure.
//
// ⭐ IT ENDS AT "SEND YOUR INVITE", NOT AT "EVENT CREATED" (ROADMAP A2b #61b, built as #62 P6g). Since #62 the wizard
// has already set up the tickets, vouchers, questions and channels, so this screen no longer points back at them
// (owner, 2026-09-29: "it shows Set up selling / Set up tickets but the user did all this already"). It hands over what
// gets guests in, by how guests join:
//
//   • open RSVP / tickets / save-the-date → the event IS the link. A draft is private, so: Publish first, then the link,
//     its QR code and one-tap posts to Facebook, LinkedIn, X, WhatsApp, Telegram, Reddit and email. With marketing
//     channels, the host picks which channel a link is FOR, and the link, QR and every post carry that channel's
//     `?ch=` — so the report can say which post brought the guests.
//   • invite-only → there is no public link to post (each guest gets their own), so the guest list comes first: with
//     people on it (the wizard's Guest list step, #62 P6c) → Send invitations; with nobody → Add guests.
//   • open RSVP / save-the-date with people on the guest list → once live, also "Send personal invitations".
//   ⚠ How many are on the list is READ, and "nobody" is said only when the read says 0 — never while it loads or failed.
//
// ⚠ The link, QR, channel picker and posts are `EventSharePanel` (ROADMAP #138) — the same panel the dashboard's Share
//   drawer shows, so the two cannot drift. It holds the share rules (`@em/blocks/shareTargets`) and the tracked link.
// ⚠ No link or QR before the event is live — a draft's link opens nothing, and handing one over reads as "go send this".
// ⚠ #148 — a quick ticket sale Finish KEPT as a draft (`publishHold`: a paid ticket and no card payments) says why, offers
//   to connect Stripe, and holds Publish until Stripe answers that charges are enabled — "publish only when it can sell".

import React, { useEffect, useMemo, useState } from 'react';
import { Box, Container, Stack, Group, Text, Title, ThemeIcon, Button, Card, Badge, UnstyledButton, Alert } from '@mantine/core';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { getRoutePath } from '@/config';
import { premiumFeaturesInUse } from '@/config/eventSetup.generated';
import { useAppPlans, useFeatureGate } from '@/hooks';
import { premiumFeaturesBlocking } from '@/utils/eventFeaturePlans';
import { PremiumFeaturesCard } from '@/components/features/EventFeatures/PremiumFeaturesCard';
import { PreviewSiteButton } from '@/components/features/Events/PreviewSiteButton';
import { UpgradeOfferBanner } from '@/components/features/Events/UpgradeOfferBanner';
import { EventSharePanel } from '@/components/features/EventShare';
import { EventSetupSteps } from '@/components/features/EventSetup';

import { InvitationQuickSend } from './InvitationQuickSend';
import { WebsiteNudgeCard } from './WebsiteNudgeCard';
import { CardPaymentsNotice, useCardPayments } from '@/components/features/CardPayments';
import type { QuickPublishHold } from '../wizardSetup';
import { AppIcon } from '@/components/generic';
import { Event, EventGuestService, EventGuestStats, EventService, WHOLE_SET } from '@/utils/api';
import { buildEventPublicUrl, refusalReason } from '@/utils';
import { publisherEmailHeld } from '@/utils/publishGate';
import { useApp } from '@/contexts/AppContext';
import { ConfirmEmailToPublish } from '@/components/features/Account/ConfirmEmailToPublish';

import { EVENT_MODE, GUEST_TRACKING, modeTakesGuestList } from '../wizardTypes';

// Mirrors EventDashboardPage — the API takes the publish state as a string enum.
const STATUS_PUBLISHED = 'published';

export type CreateResult = { ok: true; event: Event } | { ok: false; message: string };

export interface EventCreatedScreenProps {
  result: CreateResult;
  /** Return to the wizard after a failure. ⓘ Since #62 P6a a failed save stays IN the wizard, so this is a fallback. */
  onRetry: () => void;
  /** #62 P6(d) — website blocks the host saw on the website step and left unticked (never nagged about). */
  websiteDeclined?: readonly string[];
  /** #140 — the event's Sign-up list is its GIFT list (the nudge names it so, and titles its block so). */
  giftList?: boolean;
  /**
   * #139 — the quick path publishes before this screen opens. When that publish was REFUSED the event arrives as a draft
   * and this is why: shown at once, above the Publish button, so the host knows the link is not live yet.
   */
  publishError?: string | null;
  /**
   * #148 — the quick ticket sale was KEPT as a draft, and why (null: it was not held). ⚠ Required, no default: it gates
   *   Publish, and a caller that forgot it would offer Publish on a ticket nobody can pay for.
   */
  publishHold: QuickPublishHold | null;
}

const FullScreen: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    style={{
      minHeight: '100dvh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'var(--mantine-color-body)',
    }}
  >
    <Container size="xs" px="md" py="xl" w="100%">
      {children}
    </Container>
  </Box>
);

/** One tappable "go do this next" row. */
const NextStep: React.FC<{
  icon: string;
  color: string;
  title: string;
  description: string;
  onClick: () => void;
}> = ({ icon, color, title, description, onClick }) => (
  <UnstyledButton onClick={onClick} w="100%">
    <Group gap="sm" wrap="nowrap" align="flex-start">
      <ThemeIcon variant="light" color={color} size={38} radius="md">
        <AppIcon iconName={icon} size={19} />
      </ThemeIcon>
      <Stack gap={2} style={{ flex: 1 }}>
        <Text fw={600} size="md" ta="left">
          {title}
        </Text>
        <Text size="md" c="dimmed" ta="left">
          {description}
        </Text>
      </Stack>
      <AppIcon iconName="ChevronRight" size={16} />
    </Group>
  </UnstyledButton>
);

export const EventCreatedScreen: React.FC<EventCreatedScreenProps> = ({
  result,
  onRetry,
  websiteDeclined = [],
  giftList = false,
  publishError: initialPublishError = null,
  publishHold,
}) => {
  const { t } = useTranslation(['events', 'common']);
  const navigate = useNavigate();

  const event = result.ok ? result.event : null;
  const mode = event?.eventMode;
  const isInvite = mode === EVENT_MODE.MANAGED_INVITES;
  const isTicketed = mode === EVENT_MODE.TICKETED;
  const isSaveTheDate = mode === EVENT_MODE.SAVE_THE_DATE;

  const [published, setPublished] = useState(event?.eventStatus === STATUS_PUBLISHED);
  const [publishing, setPublishing] = useState(false);
  const [publishError, setPublishError] = useState<string | null>(initialPublishError);

  const baseUrl = useMemo(() => buildEventPublicUrl(event) ?? '', [event]);

  // #62 P6c — how many are on the guest list the wizard just built (the same `is_attendee` rows the Guests page lists).
  // ⚠ null = NOT KNOWN — still loading, or the read failed. Then the screen claims NEITHER way: "add your guests next"
  //   over a list the host just filled reads as "your guests are gone", and "Send invitations" over an empty one sends
  //   them to send nothing. A failed read is SAID, with a retry.
  const takesGuestList = modeTakesGuestList(mode);
  const [guestCount, setGuestCount] = useState<number | null>(null);
  const [countFailed, setCountFailed] = useState(false);
  const [countAttempt, setCountAttempt] = useState(0);

  useEffect(() => {
    if (!event?.id || !takesGuestList) {
      return;
    }

    let cancelled = false;

    setCountFailed(false);

    void EventGuestService.stats({ eventId: event.id }).then((res) => {
      if (cancelled) {
        return;
      }

      const total = res.didSucceed() ? (res.getDataFirst() as EventGuestStats | undefined)?.totalGuests : null;

      if (typeof total === 'number') {
        setGuestCount(total);
      } else {
        setCountFailed(true);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [event?.id, takesGuestList, countAttempt]);

  const hasGuests = guestCount != null && guestCount > 0;
  const noGuests = guestCount === 0;

  // #62 P6c — premium features the account's plan lacks STOP the publish (the server refuses it too). The event is
  // held here so a removal re-derives what still blocks without another read.
  const { checkFeature } = useFeatureGate();
  const { appPlans, list: listPlans } = useAppPlans({ autoLoad: false, pageSize: WHOLE_SET });
  const [liveEvent, setLiveEvent] = useState<Event | null>(event);

  useEffect(() => {
    void listPlans({}, { page: 1, pageSize: WHOLE_SET }, undefined, true);
  }, [listPlans]);

  const premiumBlocking = useMemo(
    () =>
      premiumFeaturesBlocking(premiumFeaturesInUse(liveEvent?.settings ?? null), (planFeatureCode) => {
        const verdict = checkFeature(planFeatureCode, { eventId: liveEvent?.id ?? null });

        // ROADMAP #231 — a DRAFT may use it (allowed, marked premium), but PUBLISHING asks for it; in test mode, going
        // live does — publishing is not held.
        return !verdict.allowed || (!!verdict.premium && verdict.reason === 'draft');
      }),
    [liveEvent, checkFeature],
  );
  // #148 — held for card payments until Stripe answers that charges are enabled (checked again here: the host may have
  // connected since Finish asked). ⓘ Not before the check answers either — "not known" is not "ready".
  const cardPayments = useCardPayments(!!publishHold);
  // ⚠ Not while the event is in TEST MODE (review, #198): test payments need no Stripe of the host's, and the test-purchase
  //   step says "publish, then buy with a test card" — Publish must not stay held under it.
  const sellingHeld = !!publishHold && liveEvent?.isTestMode !== true && cardPayments.status?.chargesEnabled !== true;
  // #198 a — the publisher's own email must be confirmed first (the server refuses otherwise): said, and Publish waits.
  const { user } = useApp();
  const emailHeld = !published && publisherEmailHeld(user, liveEvent);
  const publishBlocked = premiumBlocking.length > 0 || sellingHeld || emailHeld;

  const go = (suffix: string) => () => {
    if (event?.id) {
      navigate(`/events/${event.id}${suffix}`);
    }
  };

  const goDashboard = go('');
  const goList = () => navigate(getRoutePath('events'));

  /**
   * Publish the event. Resolves NULL once it is live, else the REASON it is not — "Publish & send" shows that reason in
   * its own card (the top of this screen is off-screen on a phone) and sends nothing.
   */
  const handlePublish = async (): Promise<string | null> => {
    if (!event?.id) {
      return t('events:wizard.done.publishFailed', { defaultValue: 'Could not publish the event. Please try again.' });
    }

    setPublishing(true);
    setPublishError(null);

    let reason: string | null;

    try {
      const opResult = await EventService.update({ id: event.id }, { eventStatus: STATUS_PUBLISHED });

      // ⚠ Every message, named or not — a publish refusal is named (`plan`, `slug`…) and getErrorSummary() drops it.
      reason = opResult.didSucceed()
        ? null
        : refusalReason(opResult) || t('events:wizard.done.publishFailed', { defaultValue: 'Could not publish the event. Please try again.' });
    } catch {
      // ⚠ A thrown request must settle too, or the button spins for ever.
      reason = t('events:wizard.done.publishFailed', { defaultValue: 'Could not publish the event. Please try again.' });
    }

    setPublishing(false);

    if (!reason) {
      setPublished(true);

      return null;
    }

    // Inline, not a toast: this screen is the whole viewport and the host is deciding what to do next.
    setPublishError(reason);

    return reason;
  };

  // ── Failure ────────────────────────────────────────────────────────────────
  if (!result.ok) {
    return (
      <FullScreen>
        <Stack gap="lg" align="center" ta="center">
          <ThemeIcon variant="light" color="red" size={72} radius="xl">
            <AppIcon iconName="AlertTriangle" size={36} />
          </ThemeIcon>
          <Stack gap={4} align="center">
            <Title order={2}>{t('events:wizard.done.failTitle', { defaultValue: 'Couldn’t create your event' })}</Title>
            <Text c="dimmed" size="md" maw={380}>
              {result.message}
            </Text>
          </Stack>
          <Group justify="center" gap="sm">
            <Button variant="default" size="md" onClick={goList}>
              {t('events:wizard.done.backToEvents', { defaultValue: 'Back to events' })}
            </Button>
            <Button size="md" leftSection={<AppIcon iconName="Refresh" size={16} />} onClick={onRetry}>
              {t('events:wizard.done.tryAgain', { defaultValue: 'Try again' })}
            </Button>
          </Group>
        </Stack>
      </FullScreen>
    );
  }

  // ── Success ────────────────────────────────────────────────────────────────
  // What the host does with the link, per mode — said as what is TRUE right now.
  // ⚠ Invite-only publishes FIRST too: a reply to a draft is refused ("not accepting RSVPs", PublicRsvpCore), so an
  //   invitation sent before publishing is a link that cannot be answered.
  const subtitle = isInvite
    ? !published
      ? hasGuests
        ? t('events:wizard.done.invitePublishSendSub', {
            defaultValue: 'Check the invitation below — one button publishes the event and sends it to your guests.',
          })
        : t('events:wizard.done.invitePublishSub', {
            defaultValue: 'Publish it first — invitation links open the reply form only once the event is live.',
          })
      : hasGuests
        ? t('events:wizard.done.inviteReadySub', {
            defaultValue: 'It’s live. {{count}} on your guest list — send them their invitations next.',
            count: guestCount ?? 0,
          })
        : noGuests
          ? t('events:wizard.done.inviteSub', { defaultValue: 'It’s live. Invite-only events go out to your guest list — add your guests next.' })
          : t('events:wizard.done.inviteSubUnknown', { defaultValue: 'It’s live. Invite-only events go out to your guest list.' })
    : !published
      ? sellingHeld
        ? t('events:wizard.done.heldSub', { defaultValue: 'It is saved as a draft until buyers can pay for its ticket.' })
        : t('events:wizard.done.publishSub', { defaultValue: 'Publish it to get the link, a QR code and one-tap posts.' })
      : isTicketed
        ? t('events:wizard.done.liveTicketed', { defaultValue: 'It’s live — share the link and start selling.' })
        : isSaveTheDate
          ? t('events:wizard.done.liveStd', { defaultValue: 'It’s live — share the date so people can keep it free.' })
          : // ⓘ Only where the form ASKS how many: a headcount event at one person per reply never does.
            event?.guestTracking === GUEST_TRACKING.HEADCOUNT && (event.rsvpGroupSize ?? 1) > 1
            ? t('events:wizard.done.liveHeadcount', { defaultValue: 'It’s live — share the link and guests reply with how many are coming.' })
            : t('events:wizard.done.liveRsvp', { defaultValue: 'It’s live — share the link and guests reply from it.' });

  const closingButtons = (
    <Stack gap="sm" w="100%">
      <Button fullWidth size="md" variant="light" leftSection={<AppIcon iconName="LayoutDashboard" size={18} />} onClick={goDashboard}>
        {t('events:wizard.done.goDashboard', { defaultValue: 'Go to event dashboard' })}
      </Button>
      <Button fullWidth size="md" variant="subtle" color="gray" onClick={goList}>
        {t('events:wizard.done.backToEvents', { defaultValue: 'Back to events' })}
      </Button>
    </Stack>
  );

  return (
    <FullScreen>
      <Stack gap="lg" align="center" ta="center">
        <ThemeIcon variant="light" color="emerald" size={80} radius="xl">
          <AppIcon iconName="Confetti" size={40} />
        </ThemeIcon>

        <Stack gap={6} align="center">
          <Title order={2}>
            {sellingHeld && !published
              ? t('events:wizard.done.heldTitle', { defaultValue: 'Your event is saved' })
              : t('events:wizard.done.title', { defaultValue: 'You’re all set! 🎉' })}
          </Title>
          <Text fw={600} size="md">
            {event?.title}
          </Text>
          <Badge variant="light" color={published ? 'emerald' : 'gray'} size="md">
            {published ? t('events:wizard.done.badgeLive', { defaultValue: 'Live' }) : t('events:wizard.done.badgeDraft', { defaultValue: 'Draft' })}
          </Badge>
          <Text c="dimmed" size="md">
            {subtitle}
          </Text>
        </Stack>

        {/* ROADMAP #60 (MONETIZATION §7.6) — "pay while you're here": the event's in-session upgrade offer, while it is open
            (the server's — partylabz, made from scratch, its first hour; read on its own when this screen opens). */}
        {event?.id ? <UpgradeOfferBanner eventId={event.id} onSeePlans={() => navigate(`/events/${event.id}/plans`)} /> : null}

        {publishError ? (
          <Alert variant="light" color="red" w="100%" icon={<AppIcon iconName="AlertTriangle" size={18} />}>
            <Text size="md" ta="left">
              {publishError}
            </Text>
          </Alert>
        ) : null}

        {/* ⚠ Back to the EVENT, not to this screen's URL (/events/create — a blank wizard after the detour). */}
        {emailHeld && event?.id ? <ConfirmEmailToPublish w="100%" returnTo={`/events/${event.id}`} canConfirm={!!user?.email} /> : null}

        {!published && publishHold ? (
          <Card withBorder radius="md" padding="md" w="100%" ta="left">
            <CardPaymentsNotice
              payments={cardPayments}
              paid
              offBody={t('events:wizard.done.heldCards', {
                defaultValue:
                  'Your ticket costs money, and buyers could not pay for it yet. Connect your Stripe account — the money goes straight to you — then check again and publish.',
              })}
              unknownBody={t('events:wizard.done.heldUnknown', {
                defaultValue: 'We could not check whether card payments are set up, so your event is kept as a draft. Check again, then publish.',
              })}
            />
          </Card>
        ) : null}

        {/* ROADMAP #161 b + d — the first moment for the getting-started steps: card payments when the event sells
            something paid (not here when the held sale's card above already says it), a venue, the default venue. */}
        {liveEvent ? (
          <EventSetupSteps
            event={liveEvent}
            hideCardPayments={!!publishHold}
            // #198 c — "Turn on test mode" shows the event as saved (the test-purchase step moves on to how to buy).
            onEventUpdated={(updated) => {
              if (updated) {
                setLiveEvent(updated);
              }
            }}
          />
        ) : null}

        {!published && premiumBlocking.length > 0 && liveEvent ? (
          <PremiumFeaturesCard
            event={liveEvent}
            blocking={premiumBlocking}
            plans={appPlans}
            onEventChange={setLiveEvent}
            source="wizard_finish"
          />
        ) : null}

        {/* #62 P6(d) — a feature with content and no block shows guests nothing; offer to put it on the page. */}
        {liveEvent ? <WebsiteNudgeCard event={liveEvent} declined={websiteDeclined} giftList={giftList} /> : null}

        {/* #62 P6(d4) — look before publishing or sending: a draft's site opens only through a crew preview link. */}
        {!published && event?.id ? (
          <Stack gap={4} w="100%">
            <PreviewSiteButton eventId={event.id} fullWidth />
            <Text size="md" c="dimmed">
              {t('events:wizard.done.previewHint', {
                defaultValue: 'See your event page as guests will, before you publish. Only you and your crew can open the preview.',
              })}
            </Text>
          </Stack>
        ) : null}

        {/* ── The event IS the link: publish, then share it ─────────────────── */}
        {!isInvite && !published ? (
          <>
            <Card withBorder radius="md" padding="lg" w="100%">
              <Group gap="sm" wrap="nowrap" align="flex-start">
                <ThemeIcon variant="light" color="brand" size={40} radius="md">
                  <AppIcon iconName="Send" size={20} />
                </ThemeIcon>
                <Text size="md" c="dimmed" ta="left">
                  {t('events:wizard.done.publishPrompt', {
                    defaultValue:
                      'Publishing makes the event page public, so the link and QR code work for anyone you send them to. You can keep editing after publishing.',
                  })}
                </Text>
              </Group>
            </Card>
            <Button fullWidth size="md" loading={publishing} disabled={publishBlocked} leftSection={<AppIcon iconName="Send" size={18} />} onClick={handlePublish}>
              {t('events:wizard.done.publishNow', { defaultValue: 'Publish & get the link' })}
            </Button>
            {closingButtons}
          </>
        ) : null}

        {!isInvite && published && event && baseUrl ? (
          <>
            {/* #138 — the SAME panel as the dashboard's Share drawer. The host who just made the event is its owner. */}
            <Card withBorder radius="md" padding="md" w="100%">
              <EventSharePanel event={event} baseUrl={baseUrl} channels />
            </Card>
            {/* #62 P6c — the people added on the invite list get their OWN link too. Only once live (a reply to a draft
                is refused), and not on a save-the-date, which takes no replies at all. */}
            {hasGuests && !isSaveTheDate && event.id ? (
              <InvitationQuickSend eventId={event.id} onSent={() => setCountAttempt((n) => n + 1)} onCustomize={go('/invitations')} />
            ) : null}
            {/* ⚠ Without this a failed count simply hides the send block — the host never learns they could send. */}
            {countFailed && !isSaveTheDate ? (
              <Alert variant="light" color="red" w="100%" icon={<AppIcon iconName="AlertTriangle" size={18} />}>
                <Stack gap="xs" align="flex-start">
                  <Text size="md" ta="left">
                    {t('events:wizard.done.countFailedRsvp', {
                      defaultValue: 'Could not check your guest list, so personal invitations are not offered here. Try again, or send them from Invitations.',
                    })}
                  </Text>
                  <Button variant="light" size="md" leftSection={<AppIcon iconName="Refresh" size={16} />} onClick={() => setCountAttempt((n) => n + 1)}>
                    {t('common:retry', { defaultValue: 'Try again' })}
                  </Button>
                </Stack>
              </Alert>
            ) : null}
            {closingButtons}
          </>
        ) : null}

        {!isInvite && published && !baseUrl ? (
          <Alert variant="light" color="yellow" w="100%" icon={<AppIcon iconName="AlertTriangle" size={18} />}>
            <Text size="md" ta="left">
              {t('events:wizard.done.noLink', {
                defaultValue: 'It’s live, but we could not build its link here. Open the event dashboard and use Share.',
              })}
            </Text>
          </Alert>
        ) : null}

        {/* ── Invite-only: no public link — publish, then the guest list comes first ──────── */}
        {isInvite ? (
          <>
            <Card withBorder radius="md" padding="lg" w="100%">
              <Stack gap="lg">
                <Text size="md" c="dimmed" ta="left">
                  {t('events:wizard.done.inviteNote', {
                    defaultValue:
                      'Invite-only events have no single public link — every guest gets their own personal invite link, so the guest list comes first.',
                  })}
                </Text>
                {countFailed ? (
                  <Alert variant="light" color="red" icon={<AppIcon iconName="AlertTriangle" size={18} />}>
                    <Stack gap="xs" align="flex-start">
                      <Text size="md" ta="left">
                        {t('events:wizard.done.countFailed', { defaultValue: 'Could not check how many are on your guest list.' })}
                      </Text>
                      <Button variant="light" size="md" leftSection={<AppIcon iconName="Refresh" size={16} />} onClick={() => setCountAttempt((n) => n + 1)}>
                        {t('common:retry', { defaultValue: 'Try again' })}
                      </Button>
                    </Stack>
                  </Alert>
                ) : null}
                <NextStep
                  icon="UserPlus"
                  color={noGuests ? 'brand' : 'teal'}
                  title={
                    noGuests
                      ? t('events:wizard.done.stepGuests', { defaultValue: 'Add guests' })
                      : t('events:wizard.done.stepGuestList', { defaultValue: 'Guest list' })
                  }
                  description={
                    noGuests
                      ? t('events:wizard.done.stepGuestsDesc', { defaultValue: 'Type them in, pick from your contacts, or import a list.' })
                      : hasGuests
                        ? t('events:wizard.done.stepGuestListDesc', { defaultValue: 'Add more people, or change who is in a group.' })
                        : t('events:wizard.done.stepGuestListUnknownDesc', { defaultValue: 'See who is on it, or add people.' })
                  }
                  onClick={go('/guests')}
                />
                <NextStep
                  icon="UsersGroup"
                  color="grape"
                  title={t('events:wizard.done.stepGuestGroups', { defaultValue: 'Guest groups' })}
                  description={t('events:wizard.done.stepGuestGroupsDesc', {
                    defaultValue: 'Optional — sort guests into families, tables or sides to message them separately.',
                  })}
                  onClick={go('/guests/groups')}
                />
              </Stack>
            </Card>
            {/* With guests on the list, the invitation is previewed NOW and one button publishes and sends (owner, 2026-09-29). */}
            {hasGuests && event?.id ? (
              <InvitationQuickSend
                eventId={event.id}
                onSent={() => setCountAttempt((n) => n + 1)}
                onCustomize={go('/invitations')}
                publishFirst={published ? undefined : { onPublish: handlePublish, blocked: publishBlocked }}
              />
            ) : null}
            {!published ? (
              <Button fullWidth size="md" variant={hasGuests ? 'default' : 'filled'} loading={publishing} disabled={publishBlocked} leftSection={<AppIcon iconName="Send" size={18} />} onClick={handlePublish}>
                {hasGuests
                  ? t('events:wizard.done.publishOnly', { defaultValue: 'Publish without sending' })
                  : t('events:wizard.done.publishInvite', { defaultValue: 'Publish' })}
              </Button>
            ) : hasGuests ? null : (
              <Button fullWidth size="md" leftSection={<AppIcon iconName="UserPlus" size={18} />} onClick={go('/guests')}>
                {noGuests
                  ? t('events:wizard.done.addGuests', { defaultValue: 'Add guests' })
                  : t('events:wizard.done.openGuestList', { defaultValue: 'Open the guest list' })}
              </Button>
            )}
            {closingButtons}
          </>
        ) : null}
      </Stack>
    </FullScreen>
  );
};
